const BAND_SORT_ORDER = [
  '160', '80', '40', '20', '15', '10', '6', '2', '1.25', '70cm', '33cm', '23cm',
  '13cm', '9cm', '6cm', '3cm', 'Satellite', 'Other', 'Unknown'
];

const MODE_ORDER = ['Phone', 'CW', 'Digital', 'Unknown'];

const MODE_COLORS = {
  Phone: '#558303',
  CW: '#2563eb',
  Digital: '#c2410c',
  Unknown: '#64748b'
};

export { MODE_COLORS };

export function normalizeBand(frequency) {
  if (!frequency) {
    return 'Unknown';
  }

  const key = String(frequency).trim();
  const short = key.replace(/\s*\([^)]*\)$/, '').trim();

  const aliases = {
    '70cm': '70cm',
    '70 cm': '70cm',
    '33cm': '33cm',
    '33 cm': '33cm',
    '23cm': '23cm',
    '23 cm': '23cm',
    '13cm': '13cm',
    '13 cm': '13cm',
    '9cm': '9cm',
    '9 cm': '9cm',
    '6cm': '6cm',
    '6 cm': '6cm',
    '3cm': '3cm',
    '3 cm': '3cm',
    satellite: 'Satellite',
    other: 'Other'
  };

  if (aliases[short.toLowerCase()]) {
    return aliases[short.toLowerCase()];
  }

  if (/^\d+(\.\d+)?$/.test(short)) {
    return short;
  }

  return key;
}

export function normalizeMode(mode) {
  const value = String(mode || '').trim();
  if (value === 'CW' || value === 'Phone' || value === 'Digital') {
    return value;
  }
  return value || 'Unknown';
}

export function getContactOperator(contact) {
  return contact?.operator || contact?.createdBy || 'Unknown';
}

function incrementCount(map, key, amount = 1) {
  map.set(key, (map.get(key) || 0) + amount);
}

function mapToSortedArray(map, sortOrder) {
  return Array.from(map.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => {
      const indexA = sortOrder.indexOf(a.name);
      const indexB = sortOrder.indexOf(b.name);
      if (indexA === -1 && indexB === -1) {
        return a.name.localeCompare(b.name);
      }
      if (indexA === -1) {
        return 1;
      }
      if (indexB === -1) {
        return -1;
      }
      return indexA - indexB;
    });
}

function formatMonthKey(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabel(key) {
  const [year, month] = key.split('-');
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function formatDayKey(date) {
  return date.toISOString().slice(0, 10);
}

export function computeClubStats(contacts = []) {
  const active = (contacts || []).filter((contact) => contact.deleted !== 'Y');
  const byMode = new Map();
  const byBand = new Map();
  const byBandMode = new Map();
  const byOperator = new Map();
  const byContest = new Map();
  const byMonth = new Map();
  const byDay = new Map();
  const byHour = new Map();
  const byExchange = new Map();
  const uniqueCallsigns = new Set();

  let firstTimestamp = null;
  let lastTimestamp = null;

  active.forEach((contact) => {
    const mode = normalizeMode(contact.mode);
    const band = normalizeBand(contact.frequency);
    const operator = getContactOperator(contact);
    const contest = String(contact.contestSlug || 'field-day');
    const exchange = String(contact.locationReceived || '').trim().toUpperCase();
    const callsign = String(contact.callsign || '').trim().toUpperCase();
    const timestamp = contact.timestamp ? new Date(contact.timestamp) : null;

    incrementCount(byMode, mode);
    incrementCount(byBand, band);
    incrementCount(byBandMode, `${band} · ${mode}`);
    incrementCount(byContest, contest);

    if (exchange) {
      incrementCount(byExchange, exchange);
    }
    if (callsign) {
      uniqueCallsigns.add(callsign);
    }

    if (!byOperator.has(operator)) {
      byOperator.set(operator, {
        operator,
        contacts: 0,
        phone: 0,
        cw: 0,
        digital: 0,
        points: 0,
        uniqueExchanges: new Set()
      });
    }

    const operatorStats = byOperator.get(operator);
    operatorStats.contacts += 1;
    if (mode === 'Phone') {
      operatorStats.phone += 1;
      operatorStats.points += 1;
    } else if (mode === 'CW') {
      operatorStats.cw += 1;
      operatorStats.points += 2;
    } else if (mode === 'Digital') {
      operatorStats.digital += 1;
      operatorStats.points += 2;
    }
    if (exchange) {
      operatorStats.uniqueExchanges.add(exchange);
    }

    if (timestamp && !Number.isNaN(timestamp.getTime())) {
      if (!firstTimestamp || timestamp < firstTimestamp) {
        firstTimestamp = timestamp;
      }
      if (!lastTimestamp || timestamp > lastTimestamp) {
        lastTimestamp = timestamp;
      }

      incrementCount(byMonth, formatMonthKey(timestamp));
      incrementCount(byDay, formatDayKey(timestamp));
      incrementCount(byHour, String(timestamp.getUTCHours()).padStart(2, '0'));
    }
  });

  const operatorRows = Array.from(byOperator.values())
    .map((row) => ({
      operator: row.operator,
      contacts: row.contacts,
      phone: row.phone,
      cw: row.cw,
      digital: row.digital,
      points: row.points,
      uniqueExchanges: row.uniqueExchanges.size
    }))
    .sort((a, b) => b.contacts - a.contacts || a.operator.localeCompare(b.operator));

  const topExchanges = Array.from(byExchange.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name))
    .slice(0, 15);

  const timelineByMonth = Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({
      name: formatMonthLabel(key),
      key,
      value
    }));

  const timelineByDay = Array.from(byDay.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({
      name: key,
      value
    }));

  const activityByHour = Array.from({ length: 24 }, (_, hour) => {
    const key = String(hour).padStart(2, '0');
    return {
      name: `${key}:00`,
      hour: key,
      value: byHour.get(key) || 0
    };
  });

  const bandModeRows = mapToSortedArray(byBandMode, []);
  const bandModeStack = mapToSortedArray(byBand, BAND_SORT_ORDER).map((bandRow) => {
    const row = { band: bandRow.name };
    MODE_ORDER.forEach((mode) => {
      row[mode] = byBandMode.get(`${bandRow.name} · ${mode}`) || 0;
    });
    return row;
  });

  const totalPhone = byMode.get('Phone') || 0;
  const totalCw = byMode.get('CW') || 0;
  const totalDigital = byMode.get('Digital') || 0;
  const qsoPoints = totalPhone + (totalCw * 2) + (totalDigital * 2);

  return {
    totalContacts: active.length,
    uniqueCallsigns: uniqueCallsigns.size,
    uniqueExchanges: byExchange.size,
    operatorCount: operatorRows.filter((row) => row.operator !== 'Unknown').length,
    qsoPoints,
    dateRange: {
      first: firstTimestamp ? firstTimestamp.toISOString() : null,
      last: lastTimestamp ? lastTimestamp.toISOString() : null
    },
    byMode: mapToSortedArray(byMode, MODE_ORDER).map((row) => ({
      ...row,
      fill: MODE_COLORS[row.name] || MODE_COLORS.Unknown
    })),
    byBand: mapToSortedArray(byBand, BAND_SORT_ORDER),
    byBandMode: bandModeRows,
    bandModeStack,
    byContest: mapToSortedArray(byContest, []),
    byOperator: operatorRows,
    topOperators: operatorRows.slice(0, 12),
    topExchanges,
    timelineByMonth,
    timelineByDay,
    activityByHour
  };
}

export function formatReportDateRange(dateRange) {
  if (!dateRange?.first && !dateRange?.last) {
    return 'No QSO dates recorded';
  }

  const format = (iso) => new Date(iso).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });

  if (dateRange.first && dateRange.last) {
    return `${format(dateRange.first)} – ${format(dateRange.last)} UTC`;
  }

  return format(dateRange.first || dateRange.last);
}

const CONTEST_REPORT_NAMES = {
  'field-day': 'Field Day',
  azqp: 'AZQP'
};

export function deriveReportYear(contacts = []) {
  const yearCounts = new Map();

  (contacts || []).forEach((contact) => {
    if (contact.deleted === 'Y' || !contact.timestamp) {
      return;
    }
    const year = new Date(contact.timestamp).getUTCFullYear();
    if (Number.isFinite(year)) {
      yearCounts.set(year, (yearCounts.get(year) || 0) + 1);
    }
  });

  if (yearCounts.size === 0) {
    return new Date().getUTCFullYear();
  }

  return Array.from(yearCounts.entries())
    .sort((a, b) => b[1] - a[1] || b[0] - a[0])[0][0];
}

export function suggestReportLabel(contestSlug, contacts = []) {
  const year = deriveReportYear(contacts);
  const shortYear = String(year).slice(-2);
  const contestName = CONTEST_REPORT_NAMES[contestSlug]
    || contestSlug.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  return `${shortYear} ${contestName}`;
}

export function buildReportPayload(contacts, contestSlug, stats) {
  const year = deriveReportYear(contacts);
  return {
    label: suggestReportLabel(contestSlug, contacts),
    year,
    contestSlug,
    statsData: stats,
    contactCount: stats.totalContacts,
    dateRangeFirst: stats.dateRange?.first || null,
    dateRangeLast: stats.dateRange?.last || null
  };
}
